/**
 * Canonical typed identifier constants for experience contracts.
 *
 * P2-008: Replace unsafe branded-string cast helpers with generated constants.
 * Unknown IDs must fail build-time parity tests.
 * W7: Remove unsafe ID casts (as any, as TranslationKey, as OperationId).
 */
export const ROUTE_IDS = {
    LOGIN: 'login',
    FORGOT_PASSWORD: 'forgot-password',
    RESET_PASSWORD: 'reset-password',
    MFA_VERIFY: 'mfa-verify',
    MFA_ENROLL: 'mfa-enroll',
    MFA_STEP_UP: 'mfa-step-up',
    INITIAL_PASSWORD_CHANGE: 'initial-password-change',
    DASHBOARD: 'dashboard',
    TRANSACTIONS: 'transactions',
    TRANSACTION_CREATE: 'transaction-create',
    TRANSACTION_DETAIL: 'transaction-detail',
    TRANSACTION_APPROVALS: 'transaction-approvals',
    TRANSACTION_REVERSALS: 'transaction-reversals',
    SETTINGS: 'settings',
    SETTINGS_PROFILE: 'settings-profile',
    SETTINGS_PREFERENCES: 'settings-preferences',
    SETTINGS_MFA: 'settings-mfa',
    SETTINGS_PASSWORD: 'settings-password',
    SETTINGS_SESSIONS: 'settings-sessions',
    SETTINGS_SECURITY: 'settings-security',
    HOLDINGS: 'holdings',
    REPORTS: 'reports',
    REPORT_DETAIL: 'report-detail',
    RECONCILIATION: 'reconciliation',
    CORPORATE_ACTIONS: 'corporate-actions',
    ADMIN_HUB: 'admin-hub',
    PLATFORM_ADMIN_HUB: 'platform-admin-hub',
    ORGANIZATION_ADMIN_HUB: 'organization-admin-hub',
    ORGANIZATION_GOVERNANCE_WORKSPACE: 'organization-governance-workspace',
    DATA_HUB: 'data-hub',
    DATA_INSTRUMENTS: 'data-instruments',
    DATA_INSTRUMENT_CREATE: 'data-instrument-create',
    DATA_INSTRUMENT_DETAIL: 'data-instrument-detail',
    DATA_INSTRUMENT_EDIT: 'data-instrument-edit',
    DATA_IMPORTS: 'data-imports',
    DATA_IMPORT_UPLOAD: 'data-import-upload',
    DATA_IMPORT_DETAIL: 'data-import-detail',
    DATA_RECONCILIATION: 'data-reconciliation',
    DATA_RECONCILIATION_DETAIL: 'data-reconciliation-detail',
    DATA_RECONCILIATION_CREATE: 'data-reconciliation-create',
    DATA_CORPORATE_ACTIONS: 'data-corporate-actions',
    DATA_CORPORATE_ACTION_DETAIL: 'data-corporate-action-detail',
    DATA_CORPORATE_ACTION_CREATE: 'data-corporate-action-create',
    DATA_CORPORATE_ACTION_EDIT: 'data-corporate-action-edit',
    DATA_HOLDINGS: 'data-holdings',
    DATA_HOLDINGS_DETAIL: 'data-holdings-detail',
    DATA_FACT_BATCHES: 'data-fact-batches',
    DATA_FACT_BATCH_DETAIL: 'data-fact-batch-detail',
    DATA_FACT_BATCH_CREATE: 'data-fact-batch-create',
    USERS: 'users',
    ROLES: 'roles',
    ORGANIZATION_PROFILE: 'organization-profile',
    ORGANIZATION_PROFILE_DETAIL: 'organization-profile-detail',
    ORGANIZATION_PROFILE_EDIT: 'organization-profile-edit',
    PLATFORM_ACCESS_INSPECTOR: 'platform-access-inspector',
    ORGANIZATION_ACCESS_INSPECTOR: 'organization-access-inspector',
    AUDIT_EXPLORER: 'audit-explorer',
    USERS_CREATE: 'users-create',
    USERS_DETAIL: 'users-detail',
    USERS_EDIT: 'users-edit',
    ROLES_CREATE: 'roles-create',
    ROLES_DETAIL: 'roles-detail',
    ROLES_EDIT: 'roles-edit',
    // Platform administration routes
    PLATFORM_ORGANIZATIONS_LIST: 'platform-organizations',
    PLATFORM_ORGANIZATIONS_CREATE: 'platform-organizations-create',
    PLATFORM_ORGANIZATIONS_DETAIL: 'platform-organizations-detail',
    PLATFORM_ORGANIZATIONS_EDIT: 'platform-organizations-edit',
    PLATFORM_MODULES_LIST: 'platform-modules',
    PLATFORM_MODULES_DETAIL: 'platform-modules-detail',
    PLATFORM_MODULE_ASSIGNMENTS: 'platform-module-assignments',
    PLATFORM_HEALTH: 'platform-health',
    PLATFORM_SUPPORT_DELEGATIONS_LIST: 'platform-support-delegations',
    PLATFORM_SUPPORT_DELEGATIONS_CREATE: 'platform-support-delegations-create',
    PLATFORM_SUPPORT_DELEGATIONS_DETAIL: 'platform-support-delegations-detail',
    PLATFORM_SUPPORT_DELEGATIONS_REVOKE: 'platform-support-delegations-revoke',
    PLATFORM_USERS_LIST: 'platform-users',
    PLATFORM_USERS_CREATE: 'platform-users-create',
    PLATFORM_USERS_DETAIL: 'platform-users-detail',
    PLATFORM_USERS_EDIT: 'platform-users-edit',
    PLATFORM_AUDIT_LIST: 'platform-audit',
    PLATFORM_AUDIT_DETAIL: 'platform-audit-detail',
    // Organization administration routes
    ORGANIZATION_BRANCHES_LIST: 'organization-branches',
    ORGANIZATION_BRANCHES_CREATE: 'organization-branches-create',
    ORGANIZATION_BRANCHES_DETAIL: 'organization-branches-detail',
    ORGANIZATION_BRANCHES_EDIT: 'organization-branches-edit',
    ORGANIZATION_MODULES_LIST: 'organization-modules',
    ORGANIZATION_MODULES_CREATE: 'organization-modules-create',
    ORGANIZATION_MODULES_CONFIGURE: 'organization-modules-configure',
    ORGANIZATION_USERS_LIST: 'organization-users',
    ORGANIZATION_USERS_CREATE: 'organization-users-create',
    ORGANIZATION_USERS_DETAIL: 'organization-users-detail',
    ORGANIZATION_USERS_EDIT: 'organization-users-edit',
    ORGANIZATION_ROLES_LIST: 'organization-roles',
    ORGANIZATION_ROLES_CREATE: 'organization-roles-create',
    ORGANIZATION_ROLES_DETAIL: 'organization-roles-detail',
    ORGANIZATION_ROLES_EDIT: 'organization-roles-edit',
    ORGANIZATION_PERMISSION_ASSIGNMENTS_LIST: 'organization-permission-assignments',
    ORGANIZATION_PERMISSION_ASSIGNMENTS_EDIT: 'organization-permission-assignments-edit',
    ORGANIZATION_CLIENTS_LIST: 'organization-clients',
    ORGANIZATION_CLIENTS_CREATE: 'organization-clients-create',
    ORGANIZATION_CLIENTS_DETAIL: 'organization-clients-detail',
    ORGANIZATION_CLIENTS_EDIT: 'organization-clients-edit',
    ORGANIZATION_CLIENT_ACCOUNTS_LIST: 'organization-client-accounts',
    ORGANIZATION_CLIENT_ACCOUNTS_CREATE: 'organization-client-accounts-create',
    ORGANIZATION_CLIENT_ACCOUNTS_DETAIL: 'organization-client-accounts-detail',
    ORGANIZATION_CLIENT_ACCOUNTS_EDIT: 'organization-client-accounts-edit',
    ORGANIZATION_PORTFOLIOS_LIST: 'organization-portfolios',
    ORGANIZATION_PORTFOLIOS_CREATE: 'organization-portfolios-create',
    ORGANIZATION_PORTFOLIOS_DETAIL: 'organization-portfolios-detail',
    ORGANIZATION_PORTFOLIOS_EDIT: 'organization-portfolios-edit',
    ORGANIZATION_AUDIT_LIST: 'organization-audit',
    ORGANIZATION_AUDIT_DETAIL: 'organization-audit-detail',
    // Fiscal calendar administration routes
    ADMIN_FISCAL_YEARS_LIST: 'admin-fiscal-years',
    ADMIN_FISCAL_YEAR_CREATE: 'admin-fiscal-year-create',
    ADMIN_FISCAL_YEAR_DETAIL: 'admin-fiscal-year-detail',
    ADMIN_FISCAL_YEAR_PERIODS: 'admin-fiscal-year-periods',
    // Reference data administration routes
    ADMIN_OPENING_CUTOVER_LIST: 'admin-opening-cutover',
    ADMIN_OPENING_CUTOVER_CREATE: 'admin-opening-cutover-create',
    ADMIN_OPENING_CUTOVER_DETAIL: 'admin-opening-cutover-detail',
    ADMIN_OPENING_CUTOVER_EDIT: 'admin-opening-cutover-edit',
    ADMIN_OPENING_CUTOVER_HISTORY: 'admin-opening-cutover-history',
    ADMIN_TAX_RULES_LIST: 'admin-tax-rules',
    ADMIN_TAX_RULES_CREATE: 'admin-tax-rules-create',
    ADMIN_TAX_RULES_DETAIL: 'admin-tax-rules-detail',
    ADMIN_TAX_RULES_EDIT: 'admin-tax-rules-edit',
    ADMIN_FEE_RULES_LIST: 'admin-fee-rules',
    ADMIN_FEE_RULES_CREATE: 'admin-fee-rules-create',
    ADMIN_FEE_RULES_DETAIL: 'admin-fee-rules-detail',
    ADMIN_FEE_RULES_EDIT: 'admin-fee-rules-edit',
    // Organization administration — missing capabilities (P2 hardening)
    ORGANIZATION_APPROVAL_POLICIES_LIST: 'organization-approval-policies',
    ORGANIZATION_APPROVAL_POLICIES_DETAIL: 'organization-approval-policies-detail',
    ORGANIZATION_PERMISSION_BUNDLES_LIST: 'organization-permission-bundles',
    ORGANIZATION_PERMISSION_BUNDLES_DETAIL: 'organization-permission-bundles-detail',
    ORGANIZATION_RETENTION_POLICIES: 'organization-retention-policies',
    ORGANIZATION_EXPORT_POLICIES: 'organization-export-policies',
    ORGANIZATION_SECURITY_SETTINGS: 'organization-security-settings',
    ORGANIZATION_REGULATORY_PROFILE: 'organization-regulatory-profile',
    ORGANIZATION_JURISDICTION_SETTINGS: 'organization-jurisdiction-settings',
    ORGANIZATION_SUPPORT_ACCESS: 'organization-support-access',
    // Platform administration — missing capabilities (P2 hardening)
    PLATFORM_SECURITY_POSTURE: 'platform-security-posture',
    PLATFORM_DATA_PROVIDER_HEALTH: 'platform-data-provider-health',
    PLATFORM_MAINTENANCE_DELEGATIONS_LIST: 'platform-maintenance-delegations',
    PLATFORM_MAINTENANCE_DELEGATIONS_CREATE: 'platform-maintenance-delegations-create',
    PLATFORM_MAINTENANCE_DELEGATIONS_DETAIL: 'platform-maintenance-delegations-detail',
    // Data Hub — market data group (P2 hardening — PLANNED)
    DATA_MARKET_PRICES: 'data-market-prices',
    DATA_MARKET_PRICE_DETAIL: 'data-market-price-detail',
    DATA_FX_RATES: 'data-fx-rates',
    DATA_BENCHMARKS: 'data-benchmarks',
    DATA_MARKET_DATA_PROVIDERS: 'data-market-data-providers',
    DATA_MARKET_DATA_EXCEPTIONS: 'data-market-data-exceptions',
    // Data Hub — portfolio management group (P2 hardening — PLANNED)
    DATA_CLIENTS: 'data-clients',
    DATA_CLIENT_DETAIL: 'data-client-detail',
    DATA_PORTFOLIOS: 'data-portfolios',
    DATA_PORTFOLIO_DETAIL: 'data-portfolio-detail',
    DATA_MANDATES: 'data-mandates',
    DATA_POSITIONS: 'data-positions',
    DATA_POSITIONS_DETAIL: 'data-positions-detail',
    DATA_CASH: 'data-cash',
    DATA_CASH_DETAIL: 'data-cash-detail',
    DATA_PERFORMANCE: 'data-performance',
    DATA_POSITION_TRANSFERS: 'data-position-transfers',
    DATA_MANAGEMENT_FEES: 'data-management-fees',
    DATA_PERFORMANCE_FEES: 'data-performance-fees',
    // Data Hub — settlement and custody group (P2 hardening — PLANNED)
    DATA_SETTLEMENT_INSTRUCTIONS: 'data-settlement-instructions',
    DATA_CUSTODY_BALANCES: 'data-custody-balances',
    DATA_SETTLEMENT_FAILURES: 'data-settlement-failures',
    // Data Hub — accounting group (P2 hardening — PLANNED)
    DATA_JOURNALS: 'data-journals',
    DATA_JOURNAL_DETAIL: 'data-journal-detail',
    DATA_JOURNAL_CREATE: 'data-journal-create',
    DATA_CHART_OF_ACCOUNTS: 'data-chart-of-accounts',
    DATA_FISCAL_PERIODS: 'data-fiscal-periods',
    // Data Hub — compliance and risk group (P2 hardening — PLANNED)
    DATA_COMPLIANCE_BREACHES: 'data-compliance-breaches',
    DATA_COMPLIANCE_BREACH_DETAIL: 'data-compliance-breach-detail',
    DATA_RISK_EXPOSURES: 'data-risk-exposures',
    // Data Hub — tax management group (P2 hardening — PLANNED)
    DATA_TAX_RECORDS: 'data-tax-records',
    DATA_TAX_CALCULATIONS: 'data-tax-calculations',
    // Data Hub — document and evidence group (P2 hardening — PLANNED)
    DATA_EVIDENCE: 'data-evidence',
    DATA_EVIDENCE_DETAIL: 'data-evidence-detail',
};
export const ICON_IDS = {
    DASHBOARD: 'dashboard',
    TRANSACTIONS: 'transactions',
    HOLDINGS: 'investments',
    REPORTS: 'reports',
    RECONCILIATION: 'reconciliation',
    CORPORATE_ACTIONS: 'category',
    SETTINGS: 'settings',
    ADMIN: 'security',
    SECURITY: 'security',
    DATA: 'accountBalance',
    ADD: 'add',
    EDIT: 'edit',
    DELETE: 'delete',
    SAVE: 'save',
    CANCEL: 'cancel',
    EXPORT: 'export',
    REFRESH: 'refresh',
    FILTER: 'filter',
    MORE: 'more',
    USERS: 'users',
    ORGANIZATION: 'organization',
    BATCH: 'batch',
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    CHECK_CIRCLE: 'success',
    GROUPS: 'groups',
    LOCK: 'lock',
    BUSINESS: 'business',
    RECEIPT: 'receipt',
    IMPORT: 'import',
    PERSON: 'person',
    KEY: 'key',
    TUNE: 'tune',
    HISTORY: 'history',
};
export const MODULE_KEYS = {
    AUTH: 'AUTH',
    DASHBOARD: 'DASHBOARD',
    TRANSACTIONS: 'INVESTMENT',
    HOLDINGS: 'HOLDINGS',
    SETTINGS: 'SETTINGS',
    REPORTS: 'REPORTING',
    RECONCILIATION: 'RECONCILIATION',
    CORPORATE_ACTIONS: 'CORPORATE_ACTIONS',
    ADMIN: 'ADMIN',
    PLATFORM: 'PLATFORM',
    NAVIGATION: 'NAVIGATION',
    DATA: 'DATA',
    INSTRUMENTS: 'INSTRUMENTS',
    IMPORT: 'IMPORT',
    FACTS: 'FACTS',
    MARKET_DATA: 'MARKET_DATA',
    PORTFOLIO_MGMT: 'INVESTMENT',
    SETTLEMENT: 'SETTLEMENT',
    ACCOUNTING: 'ACCOUNTING',
    TAX: 'TAX',
    COMPLIANCE: 'INVESTMENT',
    RISK: 'INVESTMENT',
    DOCUMENTS: 'IMPORT',
    ORDER_MGMT: 'INVESTMENT',
};
export const FUNCTIONAL_DOMAINS = {
    INVESTMENTS: 'investments',
    DATA_OPERATIONS: 'dataOperations',
    REPORTS_COMPLIANCE: 'reportsCompliance',
    ADMINISTRATION: 'administration',
    PLATFORM: 'platform',
};
export const MODULE_TO_DOMAIN = {
    [MODULE_KEYS.AUTH]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.DASHBOARD]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.PLATFORM]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.NAVIGATION]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.SETTINGS]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.ADMIN]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.TRANSACTIONS]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.HOLDINGS]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.RECONCILIATION]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.CORPORATE_ACTIONS]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.REPORTS]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.DATA]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.INSTRUMENTS]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.IMPORT]: FUNCTIONAL_DOMAINS.INVESTMENTS,
    [MODULE_KEYS.FACTS]: FUNCTIONAL_DOMAINS.INVESTMENTS,
};
export const DOMAIN_LABEL_KEYS = {
    [FUNCTIONAL_DOMAINS.INVESTMENTS]: 'domain.investments',
    [FUNCTIONAL_DOMAINS.DATA_OPERATIONS]: 'domain.dataOperations',
    [FUNCTIONAL_DOMAINS.REPORTS_COMPLIANCE]: 'domain.reportsCompliance',
    [FUNCTIONAL_DOMAINS.ADMINISTRATION]: 'domain.administration',
    [FUNCTIONAL_DOMAINS.PLATFORM]: 'domain.platform',
};
export const FEATURE_KEYS = {
    AUTH_LOGIN: 'auth.login',
    AUTH_FORGOT_PASSWORD: 'auth.forgotPassword',
    AUTH_RESET_PASSWORD: 'auth.resetPassword',
    AUTH_MFA_VERIFY: 'auth.mfaVerify',
    AUTH_MFA_ENROLL: 'auth.mfaEnroll',
    DASHBOARD_VIEW: 'dashboard.view',
    TRANSACTIONS_LIST: 'transactions.list',
    TRANSACTIONS_CREATE: 'transactions.create',
    TRANSACTIONS_DETAIL: 'transactions.detail',
    TRANSACTIONS_APPROVALS: 'transactions.approvals',
    TRANSACTIONS_REVERSALS: 'transactions.reversals',
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_PROFILE: 'settings.profile',
    SETTINGS_PREFERENCES: 'settings.preferences',
    SETTINGS_MFA: 'settings.mfa',
    SETTINGS_PASSWORD: 'settings.password',
    SETTINGS_SESSIONS: 'settings.sessions',
    SETTINGS_SECURITY: 'settings.security',
    HOLDINGS_VIEW: 'holdings.view',
    REPORTS_VIEW: 'reports.view',
    REPORT_DETAIL_READ: 'reports.detail.read',
    RECONCILIATION_VIEW: 'reconciliation.view',
    CORPORATE_ACTIONS_VIEW: 'corporateActions.view',
    ADMIN_HUB: 'admin.hub',
    ADMIN_USERS: 'admin.users',
    ADMIN_ROLES: 'admin.roles',
    ADMIN_ORGANIZATION: 'admin.organization',
    ADMIN_ORGANIZATION_GOVERNANCE: 'admin.organizationGovernance',
    ADMIN_ORGANIZATION_BRANCHES: 'admin.organizationBranches',
    ADMIN_ORGANIZATION_MODULES: 'admin.organizationModules',
    ADMIN_PLATFORM_ACCESS_INSPECTOR: 'admin.platformAccessInspector',
    ADMIN_ORGANIZATION_ACCESS_INSPECTOR: 'admin.organizationAccessInspector',
    ADMIN_AUDIT: 'admin.audit',
    DATA_HUB: 'data.hub',
    NAVIGATION: 'navigation',
    DATA_INSTRUMENTS: 'data.instruments',
    DATA_IMPORTS: 'data.imports',
    DATA_IMPORT_DETAIL: 'data.imports.detail',
    DATA_RECONCILIATION: 'data.reconciliation',
    DATA_RECONCILIATION_DETAIL: 'data.reconciliation.detail',
    DATA_CORPORATE_ACTIONS: 'data.corporateActions',
    DATA_CORPORATE_ACTION_DETAIL: 'data.corporateActions.detail',
    DATA_HOLDINGS: 'data.holdings',
    DATA_HOLDINGS_DETAIL: 'data.holdings.detail',
    DATA_FACT_BATCHES: 'data.factBatches',
    DATA_FACT_BATCH_DETAIL: 'data.factBatches.detail',
    // P2 hardening — new data hub groups
    DATA_MARKET_PRICES: 'data.marketPrices',
    DATA_FX_RATES: 'data.fxRates',
    DATA_BENCHMARKS: 'data.benchmarks',
    DATA_MARKET_DATA_PROVIDERS: 'data.marketDataProviders',
    DATA_MARKET_DATA_EXCEPTIONS: 'data.marketDataExceptions',
    DATA_CLIENTS: 'data.clients',
    DATA_PORTFOLIOS: 'data.portfolios',
    DATA_MANDATES: 'data.mandates',
    DATA_POSITIONS: 'data.positions',
    DATA_POSITIONS_DETAIL: 'data.positions.detail',
    DATA_CASH: 'data.cash',
    DATA_CASH_DETAIL: 'data.cash.detail',
    DATA_PERFORMANCE: 'data.performance',
    DATA_POSITION_TRANSFERS: 'data.positionTransfers',
    DATA_MANAGEMENT_FEES: 'data.managementFees',
    DATA_PERFORMANCE_FEES: 'data.performanceFees',
    DATA_SETTLEMENT_INSTRUCTIONS: 'data.settlementInstructions',
    DATA_CUSTODY_BALANCES: 'data.custodyBalances',
    DATA_JOURNALS: 'data.journals',
    DATA_CHART_OF_ACCOUNTS: 'data.chartOfAccounts',
    DATA_FISCAL_PERIODS: 'data.fiscalPeriods',
    DATA_COMPLIANCE_BREACHES: 'data.complianceBreaches',
    DATA_RISK_EXPOSURES: 'data.riskExposures',
    DATA_TAX_RECORDS: 'data.taxRecords',
    DATA_EVIDENCE: 'data.evidence',
    // P2 hardening — missing admin hub capabilities
    ADMIN_APPROVAL_POLICIES: 'admin.approvalPolicies',
    ADMIN_PERMISSION_BUNDLES: 'admin.permissionBundles',
    ADMIN_RETENTION_POLICIES: 'admin.retentionPolicies',
    ADMIN_EXPORT_POLICIES: 'admin.exportPolicies',
    ADMIN_SECURITY_SETTINGS: 'admin.securitySettings',
    ADMIN_REGULATORY_PROFILE: 'admin.regulatoryProfile',
    ADMIN_JURISDICTION_SETTINGS: 'admin.jurisdictionSettings',
    ADMIN_SUPPORT_ACCESS: 'admin.supportAccess',
    ADMIN_SECURITY_POSTURE: 'admin.securityPosture',
    ADMIN_DATA_PROVIDER_HEALTH: 'admin.dataProviderHealth',
    ADMIN_MAINTENANCE_DELEGATIONS: 'admin.maintenanceDelegations',
    ADMIN_FISCAL_CALENDAR: 'admin.fiscalCalendar',
    ADMIN_OPENING_CUTOVER: 'admin.openingCutover',
    ADMIN_TAX_RULES: 'admin.taxRules',
    ADMIN_FEE_RULES: 'admin.feeRules',
};
/** Feature keys that represent work-in-progress surfaces.
 * The web can use this to render a dev-only WIP marker without hard-coding keys.
 */
export const WIP_FEATURE_KEYS = [
    FEATURE_KEYS.ADMIN_ORGANIZATION_GOVERNANCE,
    FEATURE_KEYS.ADMIN_ORGANIZATION_MODULES,
    FEATURE_KEYS.ADMIN_AUDIT,
    FEATURE_KEYS.ADMIN_ORGANIZATION_ACCESS_INSPECTOR,
];
export const TELEMETRY_IDS = {
    AUTH_LOGIN: 'auth.login',
    AUTH_FORGOT_PASSWORD: 'auth.forgotPassword',
    AUTH_RESET_PASSWORD: 'auth.resetPassword',
    AUTH_MFA_VERIFY: 'auth.mfaVerify',
    AUTH_MFA_ENROLL: 'auth.mfaEnroll',
    DASHBOARD_VIEW: 'dashboard.view',
    TRANSACTIONS_LIST: 'transactions.list',
    TRANSACTIONS_CREATE: 'transactions.create',
    TRANSACTIONS_DETAIL: 'transactions.detail',
    TRANSACTIONS_APPROVALS: 'transactions.approvals',
    TRANSACTIONS_REVERSALS: 'transactions.reversals',
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_PROFILE: 'settings.profile',
    SETTINGS_PREFERENCES: 'settings.preferences',
    SETTINGS_MFA: 'settings.mfa',
    SETTINGS_PASSWORD: 'settings.password',
    SETTINGS_SESSIONS: 'settings.sessions',
    SETTINGS_SECURITY: 'settings.security',
    HOLDINGS_VIEW: 'holdings.view',
    REPORTS_VIEW: 'reports.view',
    REPORT_DETAIL_READ: 'reports.detail.read',
    RECONCILIATION_VIEW: 'reconciliation.view',
    CORPORATE_ACTIONS_VIEW: 'corporateActions.view',
    ADMIN_HUB: 'admin.hub',
    ADMIN_USERS: 'admin.users',
    ADMIN_USERS_CREATE: 'admin.users.create',
    ADMIN_USERS_DETAIL: 'admin.users.detail',
    ADMIN_USERS_EDIT: 'admin.users.edit',
    ADMIN_ROLES: 'admin.roles',
    ADMIN_ROLES_CREATE: 'admin.roles.create',
    ADMIN_ROLES_DETAIL: 'admin.roles.detail',
    ADMIN_ROLES_EDIT: 'admin.roles.edit',
    ADMIN_ORGANIZATION: 'admin.organization',
    ADMIN_ORGANIZATION_DETAIL: 'admin.organization.detail',
    ADMIN_ORGANIZATION_GOVERNANCE: 'admin.organizationGovernance',
    ADMIN_PLATFORM_ACCESS_INSPECTOR: 'admin.platformAccessInspector',
    ADMIN_ORGANIZATION_ACCESS_INSPECTOR: 'admin.organizationAccessInspector',
    ADMIN_AUDIT: 'admin.audit',
    DATA_HUB: 'data.hub',
    HUB_ADMIN: 'hub.admin',
    HUB_ADMIN_CORE: 'hub.admin.core',
    HUB_ADMIN_SETUP_HISTORY: 'hub.admin.setupHistory',
    HUB_DATA: 'hub.data',
    HUB_DATA_REFERENCE: 'hub.data.reference',
    HUB_DATA_OPERATIONAL: 'hub.data.operational',
    HUB_DATA_EXCEPTIONS: 'hub.data.exceptions',
    HUB_DATA_FACTS: 'hub.data.facts',
    CARD_USERS: 'card.users',
    CARD_ROLES: 'card.roles',
    CARD_ORGANIZATION: 'card.organization',
    CARD_AUDIT: 'card.audit',
    CARD_INSTRUMENTS: 'card.instruments',
    CARD_TRANSACTIONS: 'card.transactions',
    CARD_TRANSACTION_APPROVALS: 'card.transactionApprovals',
    CARD_TRANSACTION_REVERSALS: 'card.transactionReversals',
    CARD_IMPORTS: 'card.imports',
    CARD_RECONCILIATION: 'card.reconciliation',
    CARD_CORPORATE_ACTIONS: 'card.corporateActions',
    CARD_HOLDINGS: 'card.holdings',
    CARD_FACT_BATCHES: 'card.factBatches',
    CARD_BRANCHES: 'card.branches',
    CARD_MODULE_ASSIGNMENTS: 'card.moduleAssignments',
    CARD_CLIENTS: 'card.clients',
    CARD_CLIENT_ACCOUNTS: 'card.clientAccounts',
    CARD_PORTFOLIOS: 'card.portfolios',
    CARD_PERMISSION_ASSIGNMENTS: 'card.permissionAssignments',
    CARD_PLATFORM_HEALTH: 'card.platformHealth',
    CARD_PLATFORM_SUPPORT_DELEGATIONS: 'card.platformSupportDelegations',
    CARD_PLATFORM_USERS: 'card.platformUsers',
    CARD_PLATFORM_MODULES: 'card.platformModules',
    CARD_PLATFORM_AUDIT: 'card.platformAudit',
    PLATFORM_HEALTH_CHECK: 'platform.health',
    // P2 hardening — new data hub groups
    HUB_DATA_MARKET: 'hub.data.market',
    HUB_DATA_PORTFOLIO: 'hub.data.portfolio',
    HUB_DATA_SETTLEMENT: 'hub.data.settlement',
    HUB_DATA_ACCOUNTING: 'hub.data.accounting',
    HUB_DATA_COMPLIANCE: 'hub.data.compliance',
    HUB_DATA_TAX: 'hub.data.tax',
    HUB_DATA_DOCUMENTS: 'hub.data.documents',
    CARD_MARKET_PRICES: 'card.marketPrices',
    CARD_FX_RATES: 'card.fxRates',
    CARD_BENCHMARKS: 'card.benchmarks',
    CARD_MARKET_DATA_PROVIDERS: 'card.marketDataProviders',
    CARD_MARKET_DATA_EXCEPTIONS: 'card.marketDataExceptions',
    CARD_CLIENTS_DATA: 'card.clientsData',
    CARD_PORTFOLIOS_DATA: 'card.portfoliosData',
    CARD_MANDATES: 'card.mandates',
    CARD_POSITIONS: 'card.positions',
    CARD_CASH: 'card.cash',
    CARD_PERFORMANCE: 'card.performance',
    CARD_POSITION_TRANSFERS: 'card.positionTransfers',
    CARD_MANAGEMENT_FEES: 'card.managementFees',
    CARD_PERFORMANCE_FEES: 'card.performanceFees',
    CARD_SETTLEMENT_INSTRUCTIONS: 'card.settlementInstructions',
    CARD_CUSTODY_BALANCES: 'card.custodyBalances',
    CARD_JOURNALS: 'card.journals',
    CARD_CHART_OF_ACCOUNTS: 'card.chartOfAccounts',
    CARD_FISCAL_PERIODS: 'card.fiscalPeriods',
    CARD_COMPLIANCE_BREACHES: 'card.complianceBreaches',
    CARD_RISK_EXPOSURES: 'card.riskExposures',
    CARD_TAX_RECORDS: 'card.taxRecords',
    CARD_EVIDENCE: 'card.evidence',
    // P2 hardening — missing admin hub capabilities
    CARD_APPROVAL_POLICIES: 'card.approvalPolicies',
    CARD_PERMISSION_BUNDLES: 'card.permissionBundles',
    CARD_RETENTION_POLICIES: 'card.retentionPolicies',
    CARD_EXPORT_POLICIES: 'card.exportPolicies',
    CARD_SECURITY_SETTINGS: 'card.securitySettings',
    CARD_REGULATORY_PROFILE: 'card.regulatoryProfile',
    CARD_JURISDICTION_SETTINGS: 'card.jurisdictionSettings',
    CARD_SUPPORT_ACCESS: 'card.supportAccess',
    CARD_SECURITY_POSTURE: 'card.securityPosture',
    CARD_DATA_PROVIDER_HEALTH: 'card.dataProviderHealth',
    CARD_MAINTENANCE_DELEGATIONS: 'card.maintenanceDelegations',
    CARD_FISCAL_YEARS: 'card.fiscalYears',
    CARD_FISCAL_CALENDAR: 'card.fiscalCalendar',
    CARD_OPENING_CUTOVER: 'card.openingCutover',
    CARD_OPENING_CUTOVER_HISTORY: 'card.openingCutoverHistory',
    CARD_TAX_RULES: 'card.taxRules',
    CARD_FEE_RULES: 'card.feeRules',
};
export const I18N_KEYS = {
    NAVIGATION_DASHBOARD: 'nav.dashboard',
    NAVIGATION_TRANSACTIONS: 'nav.transactions',
    NAVIGATION_HOLDINGS: 'nav.holdings',
    NAVIGATION_REPORTS: 'nav.reports',
    NAVIGATION_RECONCILIATION: 'nav.reconciliation',
    NAVIGATION_REGULATORY: 'nav.regulatory',
    NAVIGATION_ADMIN: 'nav.admin',
    NAVIGATION_SETTINGS: 'nav.settings',
    AVATAR_PROFILE_PREFERENCES: 'shell:userMenu.profilePreferences',
    AVATAR_PROFILE_PREFERENCES_DESCRIPTION: 'shell:userMenu.profilePreferencesDescription',
    AVATAR_SECURITY: 'shell:userMenu.security',
    AVATAR_SECURITY_DESCRIPTION: 'shell:userMenu.securityDescription',
    NAVIGATION_CORPORATE_ACTIONS: 'nav.corporateActions',
    NAVIGATION_DATA: 'nav.data',
    AUTH_LOGIN: 'auth.login.title',
    AUTH_FORGOT_PASSWORD: 'auth.forgotPassword.title',
    AUTH_RESET_PASSWORD: 'auth.resetPassword.title',
    AUTH_MFA_VERIFY: 'auth.mfa.title',
    AUTH_MFA_ENROLL: 'auth.mfa.enroll',
    SETTINGS_PROFILE: 'settings.profile.title',
    SETTINGS_PREFERENCES: 'settings.preferences.title',
    SETTINGS_MFA: 'settings.mfa.title',
    SETTINGS_PASSWORD: 'settings.password.title',
    SETTINGS_SESSIONS: 'settings.sessions.title',
    SETTINGS_SECURITY: 'settings.security.title',
    COMMON_LOADING: 'common.loading',
    COMMON_ERROR: 'common.error',
    COMMON_EMPTY: 'common.empty',
    COMMON_FORBIDDEN: 'common.forbidden',
    COMMON_NOT_FOUND: 'common.notFound',
    COMMON_DEGRADED: 'common.degraded',
    COMMON_STALE: 'common.stale',
    COMMON_LOCKED: 'common.locked',
    COMMON_MAINTENANCE: 'common.maintenance',
    COMMON_UNAVAILABLE: 'common.unavailable',
    COMMON_HIDDEN: 'common.hidden',
    COMMON_PLANNED: 'common.planned',
    TRANSACTION_CREATE: 'transaction.create.title',
    TRANSACTION_DETAIL: 'transaction.detail.title',
    TRANSACTION_APPROVALS: 'transaction.approvals.title',
    TRANSACTION_REVERSALS: 'transaction.reversals.title',
    // Planned route disabled reasons
    HOLDINGS_PLANNED: 'holdings.planned',
    REPORTS_PLANNED: 'reports.planned',
    RECONCILIATION_PLANNED: 'reconciliation.planned',
    CORPORATE_ACTIONS_PLANNED: 'corporateActions.planned',
    HUB_ADMIN_TITLE: 'hub.admin.title',
    HUB_ADMIN_CORE_TITLE: 'hub.admin.core.title',
    HUB_ADMIN_SETUP_HISTORY_TITLE: 'hub.admin.setupHistory.title',
    HUB_ADMIN_ACCESSIBILITY: 'hub.admin.title',
    HUB_ADMIN_CORE_ACCESSIBILITY: 'hub.admin.core',
    HUB_ADMIN_SETUP_HISTORY_ACCESSIBILITY: 'hub.admin.setupHistory',
    HUB_DATA_TITLE: 'hub.data.title',
    HUB_DATA_DESCRIPTION: 'hub.data.description',
    HUB_DATA_REFERENCE_TITLE: 'hub.data.reference.title',
    HUB_DATA_OPERATIONAL_TITLE: 'hub.data.operational.title',
    HUB_DATA_EXCEPTIONS_TITLE: 'hub.data.exceptions.title',
    HUB_DATA_FACTS_TITLE: 'hub.data.facts.title',
    CARD_USERS_ACCESSIBILITY: 'card.users',
    CARD_ROLES_ACCESSIBILITY: 'card.roles',
    CARD_ORGANIZATION_ACCESSIBILITY: 'card.organization',
    CARD_ORGANIZATION_GOVERNANCE_ACCESSIBILITY: 'card.organizationGovernance',
    CARD_AUDIT_ACCESSIBILITY: 'card.audit',
    CARD_INSTRUMENTS_TITLE: 'card.instruments.title',
    CARD_INSTRUMENTS_DESCRIPTION: 'card.instruments.description',
    CARD_TRANSACTIONS_TITLE: 'card.transactions.title',
    CARD_TRANSACTIONS_DESCRIPTION: 'card.transactions.description',
    CARD_TRANSACTION_APPROVALS_TITLE: 'card.transactionApprovals.title',
    CARD_TRANSACTION_APPROVALS_DESCRIPTION: 'card.transactionApprovals.description',
    CARD_TRANSACTION_REVERSALS_TITLE: 'card.transactionReversals.title',
    CARD_TRANSACTION_REVERSALS_DESCRIPTION: 'card.transactionReversals.description',
    CARD_IMPORTS_TITLE: 'card.imports.title',
    CARD_IMPORTS_DESCRIPTION: 'card.imports.description',
    CARD_RECONCILIATION_TITLE: 'card.reconciliation.title',
    CARD_RECONCILIATION_DESCRIPTION: 'card.reconciliation.description',
    CARD_CORPORATE_ACTIONS_TITLE: 'card.corporateActions.title',
    CARD_CORPORATE_ACTIONS_DESCRIPTION: 'card.corporateActions.description',
    CARD_HOLDINGS_TITLE: 'card.holdings.title',
    CARD_HOLDINGS_DESCRIPTION: 'card.holdings.description',
    CARD_FACT_BATCHES_TITLE: 'card.factBatches.title',
    CARD_FACT_BATCHES_DESCRIPTION: 'card.factBatches.description',
    PAGE_USERS_TITLE: 'page.users.title',
    PAGE_USERS_CREATE_TITLE: 'page.users.createTitle',
    PAGE_USERS_EDIT_TITLE: 'page.users.editTitle',
    PAGE_USERS_DESCRIPTION: 'page.users.description',
    PAGE_ROLES_TITLE: 'page.roles.title',
    PAGE_ROLES_CREATE_TITLE: 'page.roles.createTitle',
    PAGE_ROLES_EDIT_TITLE: 'page.roles.editTitle',
    PAGE_ROLES_DESCRIPTION: 'page.roles.description',
    PAGE_ORGANIZATION_TITLE: 'page.organization.title',
    PAGE_ORGANIZATION_DESCRIPTION: 'page.organization.description',
    PAGE_ORGANIZATION_GOVERNANCE_TITLE: 'page.organizationGovernance.title',
    PAGE_ORGANIZATION_GOVERNANCE_DESCRIPTION: 'page.organizationGovernance.description',
    PAGE_ORGANIZATION_DETAIL_TITLE: 'page.organization.detail.title',
    PAGE_PLATFORM_ACCESS_INSPECTOR_TITLE: 'page.platformAccessInspector.title',
    PAGE_ORGANIZATION_ACCESS_INSPECTOR_TITLE: 'page.organizationAccessInspector.title',
    PAGE_AUDIT_TITLE: 'page.audit.title',
    PAGE_AUDIT_DESCRIPTION: 'page.audit.description',
    PAGE_INSTRUMENTS_TITLE: 'page.instruments.title',
    PAGE_INSTRUMENTS_DESCRIPTION: 'page.instruments.description',
    PAGE_IMPORTS_TITLE: 'page.imports.title',
    PAGE_IMPORTS_DESCRIPTION: 'page.imports.description',
    PAGE_RECONCILIATION_TITLE: 'page.reconciliation.title',
    PAGE_RECONCILIATION_DESCRIPTION: 'page.reconciliation.description',
    PAGE_CORPORATE_ACTIONS_TITLE: 'page.corporateActions.title',
    PAGE_CORPORATE_ACTIONS_DESCRIPTION: 'page.corporateActions.description',
    PAGE_HOLDINGS_TITLE: 'page.holdings.title',
    PAGE_HOLDINGS_DESCRIPTION: 'page.holdings.description',
    PAGE_REPORT_DETAIL_TITLE: 'page.reportDetail.title',
    PAGE_REPORT_DETAIL_DESCRIPTION: 'page.reportDetail.description',
    PAGE_FACT_BATCHES_TITLE: 'page.factBatches.title',
    PAGE_FACT_BATCHES_DESCRIPTION: 'page.factBatches.description',
    ACTION_USERS_CREATE: 'actions.admin.users.create.label',
    ACTION_USERS_UPDATE: 'actions.admin.users.update.label',
    ACTION_USERS_DELETE: 'actions.admin.users.delete.label',
    ACTION_ROLES_CREATE: 'actions.admin.roles.create.label',
    ACTION_ROLES_UPDATE: 'actions.admin.roles.update.label',
    ACTION_ROLES_DELETE: 'actions.admin.roles.delete.label',
    ACTION_ORGANIZATION_EDIT: 'actions.admin.organization.edit.label',
    ACTION_AUDIT_EXPORT: 'actions.admin.audit.export.label',
    PAGE_BRANCHES_TITLE: 'page.branches.title',
    PAGE_BRANCHES_DESCRIPTION: 'page.branches.description',
    PAGE_MODULE_ASSIGNMENTS_TITLE: 'page.moduleAssignments.title',
    PAGE_MODULE_ASSIGNMENTS_DESCRIPTION: 'page.moduleAssignments.description',
    PAGE_CLIENTS_TITLE: 'page.clients.title',
    PAGE_CLIENTS_DESCRIPTION: 'page.clients.description',
    PAGE_CLIENT_ACCOUNTS_TITLE: 'page.clientAccounts.title',
    PAGE_CLIENT_ACCOUNTS_DESCRIPTION: 'page.clientAccounts.description',
    PAGE_PORTFOLIOS_TITLE: 'page.portfolios.title',
    PAGE_PORTFOLIOS_DESCRIPTION: 'page.portfolios.description',
    PAGE_PERMISSION_ASSIGNMENTS_TITLE: 'page.permissionAssignments.title',
    PAGE_PERMISSION_ASSIGNMENTS_DESCRIPTION: 'page.permissionAssignments.description',
    CARD_BRANCHES_ACCESSIBILITY: 'card.branches',
    CARD_MODULE_ASSIGNMENTS_ACCESSIBILITY: 'card.moduleAssignments',
    CARD_CLIENTS_ACCESSIBILITY: 'card.clients',
    CARD_CLIENT_ACCOUNTS_ACCESSIBILITY: 'card.clientAccounts',
    CARD_PORTFOLIOS_ACCESSIBILITY: 'card.portfolios',
    CARD_PERMISSION_ASSIGNMENTS_ACCESSIBILITY: 'card.permissionAssignments',
    // Platform administration page titles
    PAGE_PLATFORM_ORGANIZATIONS_TITLE: 'page.platformOrganizations.title',
    PAGE_PLATFORM_ORGANIZATIONS_DESCRIPTION: 'page.platformOrganizations.description',
    PAGE_PLATFORM_ORGANIZATIONS_DETAIL_TITLE: 'page.platformOrganizations.detail.title',
    PAGE_PLATFORM_MODULES_TITLE: 'page.platformModules.title',
    PAGE_PLATFORM_MODULES_DESCRIPTION: 'page.platformModules.description',
    PAGE_PLATFORM_HEALTH_TITLE: 'page.platformHealth.title',
    PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE: 'page.platformSupportDelegations.title',
    PAGE_PLATFORM_SUPPORT_DELEGATIONS_DESCRIPTION: 'page.platformSupportDelegations.description',
    PAGE_PLATFORM_USERS_TITLE: 'page.platformUsers.title',
    PAGE_PLATFORM_USERS_DESCRIPTION: 'page.platformUsers.description',
    PAGE_PLATFORM_USERS_CREATE_TITLE: 'page.platformUsers.createTitle',
    PAGE_PLATFORM_USERS_EDIT_TITLE: 'page.platformUsers.editTitle',
    PAGE_PLATFORM_AUDIT_TITLE: 'page.platformAudit.title',
    PAGE_PLATFORM_AUDIT_DESCRIPTION: 'page.platformAudit.description',
    PAGE_PLATFORM_HEALTH_DESCRIPTION: 'page.platformHealth.description',
    CARD_PLATFORM_HEALTH_ACCESSIBILITY: 'card.platformHealth',
    CARD_PLATFORM_SUPPORT_DELEGATIONS_ACCESSIBILITY: 'card.platformSupportDelegations',
    CARD_PLATFORM_USERS_ACCESSIBILITY: 'card.platformUsers',
    CARD_PLATFORM_MODULES_ACCESSIBILITY: 'card.platformModules',
    CARD_PLATFORM_AUDIT_ACCESSIBILITY: 'card.platformAudit',
    // Organization administration page titles
    PAGE_ORGANIZATION_BRANCHES_CREATE_TITLE: 'page.organizationBranches.createTitle',
    PAGE_ORGANIZATION_BRANCHES_EDIT_TITLE: 'page.organizationBranches.editTitle',
    PAGE_ORGANIZATION_USERS_TITLE: 'page.organizationUsers.title',
    PAGE_ORGANIZATION_USERS_DESCRIPTION: 'page.organizationUsers.description',
    PAGE_ORGANIZATION_USERS_CREATE_TITLE: 'page.organizationUsers.createTitle',
    PAGE_ORGANIZATION_USERS_EDIT_TITLE: 'page.organizationUsers.editTitle',
    PAGE_ORGANIZATION_ROLES_TITLE: 'page.organizationRoles.title',
    PAGE_ORGANIZATION_ROLES_DESCRIPTION: 'page.organizationRoles.description',
    PAGE_ORGANIZATION_ROLES_CREATE_TITLE: 'page.organizationRoles.createTitle',
    PAGE_ORGANIZATION_ROLES_EDIT_TITLE: 'page.organizationRoles.editTitle',
    PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE: 'page.organizationPermissionAssignments.title',
    PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_DESCRIPTION: 'page.organizationPermissionAssignments.description',
    PAGE_ORGANIZATION_CLIENTS_CREATE_TITLE: 'page.organizationClients.createTitle',
    PAGE_ORGANIZATION_CLIENTS_EDIT_TITLE: 'page.organizationClients.editTitle',
    PAGE_ORGANIZATION_CLIENT_ACCOUNTS_CREATE_TITLE: 'page.organizationClientAccounts.createTitle',
    PAGE_ORGANIZATION_CLIENT_ACCOUNTS_EDIT_TITLE: 'page.organizationClientAccounts.editTitle',
    PAGE_ORGANIZATION_PORTFOLIOS_CREATE_TITLE: 'page.organizationPortfolios.createTitle',
    PAGE_ORGANIZATION_PORTFOLIOS_EDIT_TITLE: 'page.organizationPortfolios.editTitle',
    PAGE_ORGANIZATION_AUDIT_TITLE: 'page.organizationAudit.title',
    PAGE_ORGANIZATION_AUDIT_DESCRIPTION: 'page.organizationAudit.description',
    PAGE_FISCAL_YEARS_TITLE: 'page.fiscalYears.title',
    PAGE_FISCAL_YEARS_DESCRIPTION: 'page.fiscalYears.description',
    PAGE_FISCAL_YEAR_DETAIL_TITLE: 'page.fiscalYears.detail.title',
    PAGE_FISCAL_YEAR_CREATE_TITLE: 'page.fiscalYears.createTitle',
    PAGE_FISCAL_YEAR_PERIODS_TITLE: 'page.fiscalYears.periods.title',
    CARD_FISCAL_CALENDAR_TITLE: 'card.fiscalCalendar.title',
    CARD_FISCAL_CALENDAR_DESCRIPTION: 'card.fiscalCalendar.description',
    CARD_FISCAL_CALENDAR_ACCESSIBILITY: 'card.fiscalCalendar',
    CARD_FISCAL_YEARS_TITLE: 'card.fiscalYears.title',
    CARD_FISCAL_YEARS_DESCRIPTION: 'card.fiscalYears.description',
    PAGE_OPENING_CUTOVER_TITLE: 'page.openingCutover.title',
    PAGE_OPENING_CUTOVER_DESCRIPTION: 'page.openingCutover.description',
    PAGE_OPENING_CUTOVER_HISTORY_TITLE: 'page.openingCutoverHistory.title',
    PAGE_OPENING_CUTOVER_HISTORY_DESCRIPTION: 'page.openingCutoverHistory.description',
    PAGE_TAX_RULES_TITLE: 'page.taxRules.title',
    PAGE_TAX_RULES_DESCRIPTION: 'page.taxRules.description',
    PAGE_FEE_RULES_TITLE: 'page.feeRules.title',
    PAGE_FEE_RULES_DESCRIPTION: 'page.feeRules.description',
    // P2 hardening — new data hub I18N keys
    HUB_DATA_MARKET_TITLE: 'hub.data.market.title',
    HUB_DATA_PORTFOLIO_TITLE: 'hub.data.portfolio.title',
    HUB_DATA_SETTLEMENT_TITLE: 'hub.data.settlement.title',
    HUB_DATA_ACCOUNTING_TITLE: 'hub.data.accounting.title',
    HUB_DATA_COMPLIANCE_TITLE: 'hub.data.compliance.title',
    HUB_DATA_TAX_TITLE: 'hub.data.tax.title',
    HUB_DATA_DOCUMENTS_TITLE: 'hub.data.documents.title',
    CARD_MARKET_PRICES_TITLE: 'card.marketPrices.title',
    CARD_MARKET_PRICES_DESCRIPTION: 'card.marketPrices.description',
    CARD_FX_RATES_TITLE: 'card.fxRates.title',
    CARD_FX_RATES_DESCRIPTION: 'card.fxRates.description',
    CARD_BENCHMARKS_TITLE: 'card.benchmarks.title',
    CARD_BENCHMARKS_DESCRIPTION: 'card.benchmarks.description',
    CARD_MARKET_DATA_PROVIDERS_TITLE: 'card.marketDataProviders.title',
    CARD_MARKET_DATA_PROVIDERS_DESCRIPTION: 'card.marketDataProviders.description',
    CARD_MARKET_DATA_EXCEPTIONS_TITLE: 'card.marketDataExceptions.title',
    CARD_MARKET_DATA_EXCEPTIONS_DESCRIPTION: 'card.marketDataExceptions.description',
    CARD_CLIENTS_DATA_TITLE: 'card.clientsData.title',
    CARD_CLIENTS_DATA_DESCRIPTION: 'card.clientsData.description',
    CARD_PORTFOLIOS_DATA_TITLE: 'card.portfoliosData.title',
    CARD_PORTFOLIOS_DATA_DESCRIPTION: 'card.portfoliosData.description',
    CARD_MANDATES_TITLE: 'card.mandates.title',
    CARD_MANDATES_DESCRIPTION: 'card.mandates.description',
    CARD_POSITIONS_TITLE: 'card.positions.title',
    CARD_POSITIONS_DESCRIPTION: 'card.positions.description',
    CARD_CASH_TITLE: 'card.cash.title',
    CARD_CASH_DESCRIPTION: 'card.cash.description',
    CARD_PERFORMANCE_TITLE: 'card.performance.title',
    CARD_PERFORMANCE_DESCRIPTION: 'card.performance.description',
    CARD_POSITION_TRANSFERS_TITLE: 'card.positionTransfers.title',
    CARD_POSITION_TRANSFERS_DESCRIPTION: 'card.positionTransfers.description',
    CARD_MANAGEMENT_FEES_TITLE: 'card.managementFees.title',
    CARD_MANAGEMENT_FEES_DESCRIPTION: 'card.managementFees.description',
    CARD_PERFORMANCE_FEES_TITLE: 'card.performanceFees.title',
    CARD_PERFORMANCE_FEES_DESCRIPTION: 'card.performanceFees.description',
    CARD_SETTLEMENT_INSTRUCTIONS_TITLE: 'card.settlementInstructions.title',
    CARD_SETTLEMENT_INSTRUCTIONS_DESCRIPTION: 'card.settlementInstructions.description',
    CARD_CUSTODY_BALANCES_TITLE: 'card.custodyBalances.title',
    CARD_CUSTODY_BALANCES_DESCRIPTION: 'card.custodyBalances.description',
    CARD_JOURNALS_TITLE: 'card.journals.title',
    CARD_JOURNALS_DESCRIPTION: 'card.journals.description',
    CARD_CHART_OF_ACCOUNTS_TITLE: 'card.chartOfAccounts.title',
    CARD_CHART_OF_ACCOUNTS_DESCRIPTION: 'card.chartOfAccounts.description',
    CARD_FISCAL_PERIODS_TITLE: 'card.fiscalPeriods.title',
    CARD_FISCAL_PERIODS_DESCRIPTION: 'card.fiscalPeriods.description',
    CARD_COMPLIANCE_BREACHES_TITLE: 'card.complianceBreaches.title',
    CARD_COMPLIANCE_BREACHES_DESCRIPTION: 'card.complianceBreaches.description',
    CARD_RISK_EXPOSURES_TITLE: 'card.riskExposures.title',
    CARD_RISK_EXPOSURES_DESCRIPTION: 'card.riskExposures.description',
    CARD_TAX_RECORDS_TITLE: 'card.taxRecords.title',
    CARD_TAX_RECORDS_DESCRIPTION: 'card.taxRecords.description',
    CARD_EVIDENCE_TITLE: 'card.evidence.title',
    CARD_EVIDENCE_DESCRIPTION: 'card.evidence.description',
    // P2 hardening — missing admin hub I18N keys
    PAGE_APPROVAL_POLICIES_TITLE: 'page.approvalPolicies.title',
    PAGE_APPROVAL_POLICIES_DESCRIPTION: 'page.approvalPolicies.description',
    PAGE_PERMISSION_BUNDLES_TITLE: 'page.permissionBundles.title',
    PAGE_PERMISSION_BUNDLES_DESCRIPTION: 'page.permissionBundles.description',
    PAGE_RETENTION_POLICIES_TITLE: 'page.retentionPolicies.title',
    PAGE_RETENTION_POLICIES_DESCRIPTION: 'page.retentionPolicies.description',
    PAGE_EXPORT_POLICIES_TITLE: 'page.exportPolicies.title',
    PAGE_EXPORT_POLICIES_DESCRIPTION: 'page.exportPolicies.description',
    PAGE_SECURITY_SETTINGS_TITLE: 'page.securitySettings.title',
    PAGE_SECURITY_SETTINGS_DESCRIPTION: 'page.securitySettings.description',
    PAGE_REGULATORY_PROFILE_TITLE: 'page.regulatoryProfile.title',
    PAGE_REGULATORY_PROFILE_DESCRIPTION: 'page.regulatoryProfile.description',
    PAGE_JURISDICTION_SETTINGS_TITLE: 'page.jurisdictionSettings.title',
    PAGE_JURISDICTION_SETTINGS_DESCRIPTION: 'page.jurisdictionSettings.description',
    PAGE_SUPPORT_ACCESS_TITLE: 'page.supportAccess.title',
    PAGE_SUPPORT_ACCESS_DESCRIPTION: 'page.supportAccess.description',
    PAGE_SECURITY_POSTURE_TITLE: 'page.securityPosture.title',
    PAGE_SECURITY_POSTURE_DESCRIPTION: 'page.securityPosture.description',
    PAGE_DATA_PROVIDER_HEALTH_TITLE: 'page.dataProviderHealth.title',
    PAGE_DATA_PROVIDER_HEALTH_DESCRIPTION: 'page.dataProviderHealth.description',
    PAGE_MAINTENANCE_DELEGATIONS_TITLE: 'page.maintenanceDelegations.title',
    PAGE_MAINTENANCE_DELEGATIONS_DESCRIPTION: 'page.maintenanceDelegations.description',
    CARD_APPROVAL_POLICIES_ACCESSIBILITY: 'card.approvalPolicies',
    CARD_PERMISSION_BUNDLES_ACCESSIBILITY: 'card.permissionBundles',
    CARD_RETENTION_POLICIES_ACCESSIBILITY: 'card.retentionPolicies',
    CARD_EXPORT_POLICIES_ACCESSIBILITY: 'card.exportPolicies',
    CARD_SECURITY_SETTINGS_ACCESSIBILITY: 'card.securitySettings',
    CARD_REGULATORY_PROFILE_ACCESSIBILITY: 'card.regulatoryProfile',
    CARD_JURISDICTION_SETTINGS_ACCESSIBILITY: 'card.jurisdictionSettings',
    CARD_SUPPORT_ACCESS_ACCESSIBILITY: 'card.supportAccess',
    CARD_SECURITY_POSTURE_ACCESSIBILITY: 'card.securityPosture',
    CARD_DATA_PROVIDER_HEALTH_ACCESSIBILITY: 'card.dataProviderHealth',
    CARD_MAINTENANCE_DELEGATIONS_ACCESSIBILITY: 'card.maintenanceDelegations',
    CARD_OPENING_CUTOVER_ACCESSIBILITY: 'card.openingCutover',
    CARD_OPENING_CUTOVER_HISTORY_ACCESSIBILITY: 'card.openingCutoverHistory',
    CARD_TAX_RULES_ACCESSIBILITY: 'card.taxRules',
    CARD_FEE_RULES_ACCESSIBILITY: 'card.feeRules',
};
export const OPERATION_IDS = {
    ACCOUNTING_PERFORMANCE_FEE_LIST: 'accounting.performance-fee.list',
    AUTH_LOGIN: 'auth.login',
    AUTH_PASSWORD_FORGOT: 'auth.password.forgot',
    AUTH_PASSWORD_RESET: 'auth.password.reset',
    AUTH_PASSWORD_CHANGE: 'auth.password.change',
    AUTH_SESSION_MANAGE: 'auth.sessions.manage',
    AUTH_CHALLENGE_TOTP: 'auth.challenge.totp',
    AUTH_ENROLL_MFA: 'auth.mfa.enroll',
    AUTH_VERIFY_MFA: 'auth.mfa.verify',
    AUTH_DISABLE_MFA: 'auth.mfa.disable',
    AUTH_READ_PROFILE: 'auth.readProfile',
    AUTH_SWITCH_CONTEXT: 'auth.switchContext',
    NAVIGATION_READ: 'navigation.read',
    TRANSACTION_LIST: 'investment.transaction.list',
    TRANSACTION_EXPORT: 'investment.transaction.export',
    TRANSACTION_CREATE: 'investment.transaction.create',
    TRANSACTION_GET: 'investment.transaction.get',
    TRANSACTION_APPROVE: 'investment.transaction.approve',
    TRANSACTION_REVERSE: 'investment.transaction.reverse',
    DASHBOARD_VIEW: 'dashboard.view',
    HOLDINGS_VIEW: 'holdings.view',
    REPORTS_VIEW: 'reports.view',
    REPORT_DETAIL_READ: 'reports.detail.read',
    RECONCILIATION_VIEW: 'reconciliation.view',
    CORPORATE_ACTIONS_VIEW: 'corporateActions.view',
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_PROFILE: 'settings.profile',
    SETTINGS_PREFERENCES: 'settings.preferences',
    SETTINGS_MFA: 'settings.mfa',
    SETTINGS_SECURITY: 'settings.security',
    ADMIN_HUB: 'admin.hub',
    ADMIN_HUB_PLATFORM: 'admin.hub.platform',
    ADMIN_HUB_ORGANIZATION: 'admin.hub.organization',
    ORGANIZATION_GOVERNANCE_WORKSPACE_READ: 'organization.governance.workspace.read',
    ADMIN_USERS_VIEW: 'admin.users.view',
    ADMIN_USERS_DETAIL: 'admin.users.detail',
    ADMIN_ROLES_VIEW: 'admin.roles.view',
    ADMIN_ROLES_DETAIL: 'admin.roles.detail',
    ADMIN_ORGANIZATION_VIEW: 'admin.organization.view',
    ADMIN_AUDIT_VIEW: 'admin.audit.view',
    ADMIN_AUDIT_LOG_LIST: 'admin.auditLog.list',
    ADMIN_MODULES_LIST: 'admin.modules.list',
    ADMIN_USER_CREATE: 'admin.user.create',
    ADMIN_USER_UPDATE: 'admin.user.update',
    ADMIN_USER_DELETE: 'admin.user.delete',
    ADMIN_ROLE_CREATE: 'admin.role.create',
    ADMIN_ROLE_UPDATE: 'admin.role.update',
    ADMIN_ROLE_DELETE: 'admin.role.delete',
    DATA_HUB: 'data.hub',
    INSTRUMENT_LIST: 'investment.instrument.list',
    INSTRUMENT_GET: 'investment.instrument.get',
    INSTRUMENT_CREATE: 'investment.instrument.create',
    INSTRUMENT_UPDATE: 'investment.instrument.update',
    MARKET_DATA_CREATE: 'investment.marketData.create',
    MARKET_DATA_LIST: 'investment.marketData.list',
    MARKET_DATA_GET: 'investment.marketData.get',
    MARKET_DATA_UPDATE: 'investment.marketData.update',
    MARKET_DATA_DELETE: 'investment.marketData.delete',
    MARKET_DATA_BULK_UPSERT: 'investment.marketData.bulkUpsert',
    IMPORT_LIST: 'import.list',
    IMPORT_GET: 'import.get',
    IMPORT_UPLOAD: 'import.upload',
    IMPORT_COMMIT: 'import.commit',
    IMPORT_VALIDATE: 'import.validate',
    RECONCILIATION_LIST: 'accounting.reconciliation.list',
    RECONCILIATION_GET: 'accounting.reconciliation.get',
    RECONCILIATION_CREATE: 'accounting.reconciliation.create',
    RECONCILIATION_RUN_CANCEL: 'reconciliation.run.cancel',
    RECONCILIATION_RUN_RETRY: 'reconciliation.run.retry',
    RECONCILIATION_RUN_BREAKS_LIST: 'reconciliation.run.breaks.list',
    RECONCILIATION_RUN_MATCHES_LIST: 'reconciliation.run.matches.list',
    ACCESS_READ_MEMBERS: 'access.members.read',
    ACCESS_READ_PERMISSIONS: 'access.permissions.read',
    ACCESS_MANAGE_MEMBERSHIP: 'access.membership.manage',
    ACCESS_READ_ROLES: 'access.roles.read',
    ACCESS_MANAGE_ROLES: 'access.roles.manage',
    PLATFORM_HEALTH_CHECK: 'platform.health.check',
    PLATFORM_SUPPORT_DELEGATIONS_LIST: 'platform.support-delegations.list',
    ORGANIZATION_PROFILE_READ: 'organization.profile.read',
    ORGANIZATION_PROFILE_DETAIL_READ: 'organization.profile.detail.read',
    ORGANIZATION_PROFILE_UPDATE: 'organization.profile.update',
    PLATFORM_ORGANIZATIONS_LIST: 'platform.organizations.list',
    PLATFORM_ORGANIZATIONS_READ: 'platform.organizations.read',
    PLATFORM_ORGANIZATIONS_CREATE: 'platform.organizations.create',
    PLATFORM_ORGANIZATIONS_UPDATE: 'platform.organizations.update',
    PLATFORM_ORGANIZATIONS_DISABLE: 'platform.organizations.disable',
    PLATFORM_ORGANIZATIONS_OFFBOARDING_EXPORT: 'platform.organizations.offboarding-export',
    PLATFORM_MODULES_LIST: 'platform.modules.list',
    PLATFORM_MODULES_READ: 'platform.modules.read',
    PLATFORM_MODULES_ASSIGN: 'platform.modules.assign',
    PLATFORM_MODULES_REVOKE: 'platform.modules.revoke',
    ORGANIZATION_BRANCHES_LIST: 'organization.branches.list',
    ORGANIZATION_BRANCHES_READ: 'organization.branches.read',
    ORGANIZATION_BRANCHES_CREATE: 'organization.branches.create',
    ORGANIZATION_BRANCHES_UPDATE: 'organization.branches.update',
    ORGANIZATION_BRANCHES_DISABLE: 'organization.branches.disable',
    ORGANIZATION_BRANCHES_MAKE_DEFAULT: 'organization.branches.make-default',
    ORGANIZATION_MODULES_LIST_ASSIGNED: 'organization.modules.list-assigned',
    ORGANIZATION_MODULES_ENABLE: 'organization.modules.enable',
    ORGANIZATION_MODULES_DISABLE: 'organization.modules.disable',
    ORGANIZATION_MODULES_CONFIGURE: 'organization.modules.configure',
    ORGANIZATION_CLIENTS_LIST: 'organization.clients.list',
    ORGANIZATION_CLIENTS_READ: 'organization.clients.read',
    ORGANIZATION_CLIENTS_CREATE: 'organization.clients.create',
    ORGANIZATION_CLIENTS_UPDATE: 'organization.clients.update',
    ORGANIZATION_CLIENTS_DISABLE: 'organization.clients.disable',
    ORGANIZATION_CLIENT_ACCOUNTS_LIST: 'organization.client-accounts.list',
    ORGANIZATION_CLIENT_ACCOUNTS_READ: 'organization.client-accounts.read',
    ORGANIZATION_CLIENT_ACCOUNTS_CREATE: 'organization.client-accounts.create',
    ORGANIZATION_CLIENT_ACCOUNTS_UPDATE: 'organization.client-accounts.update',
    ORGANIZATION_CLIENT_ACCOUNTS_DISABLE: 'organization.client-accounts.disable',
    ORGANIZATION_PORTFOLIOS_LIST: 'organization.portfolios.list',
    ORGANIZATION_PORTFOLIOS_READ: 'organization.portfolios.read',
    ORGANIZATION_PORTFOLIOS_CREATE: 'organization.portfolios.create',
    ORGANIZATION_PORTFOLIOS_UPDATE: 'organization.portfolios.update',
    ORGANIZATION_PORTFOLIOS_DISABLE: 'organization.portfolios.disable',
    CLIENT_DASHBOARD_READ: 'client.dashboard.read',
    CLIENT_PROFILE_READ: 'client.profile.read',
    CLIENT_PROFILE_UPDATE_LIMITED: 'client.profile.update-limited',
    CLIENT_HOLDINGS_READ_OWN: 'client.holdings.read-own',
    CLIENT_TRANSACTIONS_READ_OWN: 'client.transactions.read-own',
    CLIENT_REPORTS_READ_OWN: 'client.reports.read-own',
    CLIENT_REPORTS_EXPORT_OWN: 'client.reports.export-own',
    CLIENT_DOCUMENTS_READ_OWN: 'client.documents.read-own',
    ADMIN_AUDIT_EXPORT: 'admin.audit.export',
    CORPORATE_ACTION_LIST: 'investment.corporateAction.list',
    CORPORATE_ACTION_GET: 'investment.corporateAction.get',
    CORPORATE_ACTION_CREATE: 'investment.corporateAction.create',
    CORPORATE_ACTION_UPDATE: 'investment.corporateAction.update',
    CORPORATE_ACTION_SUBMIT: 'investment.corporateAction.submit',
    CORPORATE_ACTION_APPROVE: 'investment.corporateAction.approve',
    CORPORATE_ACTION_REJECT: 'investment.corporateAction.reject',
    CORPORATE_ACTION_CANCEL: 'investment.corporateAction.cancel',
    CORPORATE_ACTION_PROCESS: 'investment.corporateAction.process',
    FACT_LIST: 'investment.fact.list',
    FACT_GET: 'investment.fact.get',
    FACT_CREATE: 'investment.fact.create',
    FACT_BATCHES_LIST: 'investment.fact.batches.list',
    FACT_BATCH_GET: 'investment.fact.batch.get',
    // Platform user operations
    PLATFORM_USERS_LIST: 'platform.users.list',
    PLATFORM_USERS_READ: 'platform.users.read',
    PLATFORM_USERS_CREATE: 'platform.users.create',
    PLATFORM_USERS_UPDATE: 'platform.users.update',
    PLATFORM_USERS_DISABLE: 'platform.users.disable',
    // Organization user operations
    ORGANIZATION_USERS_LIST: 'organization.users.list',
    ORGANIZATION_USERS_READ: 'organization.users.read',
    ORGANIZATION_USERS_CREATE: 'organization.users.create',
    ORGANIZATION_USERS_UPDATE: 'organization.users.update',
    ORGANIZATION_USERS_DISABLE: 'organization.users.disable',
    // Organization role operations
    ORGANIZATION_ROLES_LIST: 'organization.roles.list',
    ORGANIZATION_ROLES_READ: 'organization.roles.read',
    ORGANIZATION_ROLES_CREATE: 'organization.roles.create',
    ORGANIZATION_ROLES_UPDATE: 'organization.roles.update',
    ORGANIZATION_ROLES_DISABLE: 'organization.roles.disable',
    // Organization permission assignment operations
    ORGANIZATION_PERMISSION_ASSIGNMENTS_LIST: 'organization.permission-assignments.list',
    ORGANIZATION_PERMISSION_ASSIGNMENTS_EDIT: 'organization.permission-assignments.edit',
    // Platform and organization audit operations
    PLATFORM_AUDIT_LIST: 'platform.audit.list',
    PLATFORM_AUDIT_READ: 'platform.audit.read',
    PLATFORM_AUDIT_EXPORT: 'platform.audit.export',
    ORGANIZATION_AUDIT_LIST: 'organization.audit.list',
    ORGANIZATION_AUDIT_READ: 'organization.audit.read',
    ORGANIZATION_AUDIT_EXPORT: 'organization.audit.export',
    // Platform support delegation operations
    PLATFORM_SUPPORT_DELEGATIONS_CREATE: 'platform.support-delegations.create',
    PLATFORM_SUPPORT_DELEGATIONS_READ: 'platform.support-delegations.read',
    PLATFORM_SUPPORT_DELEGATIONS_REVOKE: 'platform.support-delegations.revoke',
    // P2 hardening — maintenance delegation operations
    PLATFORM_MAINTENANCE_DELEGATIONS_LIST: 'platform.maintenance-delegations.list',
    PLATFORM_MAINTENANCE_DELEGATIONS_CREATE: 'platform.maintenance-delegations.create',
    PLATFORM_MAINTENANCE_DELEGATIONS_READ: 'platform.maintenance-delegations.read',
    PLATFORM_MAINTENANCE_DELEGATIONS_APPROVE: 'platform.maintenance-delegations.approve',
    PLATFORM_MAINTENANCE_DELEGATIONS_REVOKE: 'platform.maintenance-delegations.revoke',
    // P2 hardening — market data operations
    MARKET_PRICES_LIST: 'market.prices.list',
    MARKET_PRICES_GET: 'market.prices.get',
    MARKET_PRICES_OVERRIDE: 'market.prices.override',
    FX_RATES_LIST: 'market.fxRates.list',
    BENCHMARKS_LIST: 'market.benchmarks.list',
    MARKET_DATA_PROVIDERS_LIST: 'market.providers.list',
    MARKET_DATA_EXCEPTIONS_LIST: 'market.exceptions.list',
    // P2 hardening — portfolio management operations (org-admin data view)
    DATA_CLIENTS_LIST: 'investment.clients.list',
    DATA_CLIENTS_GET: 'investment.clients.get',
    DATA_PORTFOLIOS_LIST: 'investment.portfolios.list',
    DATA_PORTFOLIOS_GET: 'investment.portfolios.get',
    PORTFOLIO_ACCOUNT_LIST: 'investment.portfolioAccount.list',
    DATA_MANDATES_LIST: 'investment.mandates.list',
    DATA_POSITIONS_LIST: 'investment.positions.list',
    DATA_POSITIONS_GET: 'investment.positions.get',
    DATA_CASH_LIST: 'investment.cash.list',
    DATA_CASH_GET: 'investment.cash.get',
    DATA_PERFORMANCE_LIST: 'investment.performance.list',
    DATA_PERFORMANCE_EXPORT: 'investment.performance.export',
    POSITION_TRANSFERS_LIST: 'investment.position-transfer.list',
    POSITION_TRANSFERS_GET: 'investment.position-transfer.get',
    POSITION_TRANSFERS_ELIGIBILITY: 'investment.position-transfer.eligibility',
    POSITION_TRANSFERS_REQUEST_ELIGIBILITY: 'investment.position-transfer.request.eligibility',
    POSITION_TRANSFERS_REQUEST: 'investment.position-transfer.request',
    POSITION_TRANSFERS_APPROVE: 'investment.position-transfer.approve',
    POSITION_TRANSFERS_EXECUTE: 'investment.position-transfer.execute',
    POSITION_TRANSFERS_CANCEL: 'investment.position-transfer.cancel',
    POSITION_TRANSFERS_REVERSE: 'investment.position-transfer.reverse',
    // P2 hardening — settlement and custody operations
    SETTLEMENT_INSTRUCTIONS_LIST: 'settlement.instructions.list',
    SETTLEMENT_INSTRUCTIONS_GET: 'settlement.instructions.get',
    SETTLEMENT_INSTRUCTIONS_CREATE: 'settlement.instructions.create',
    SETTLEMENT_INSTRUCTIONS_CONFIRM: 'settlement.instructions.confirm',
    SETTLEMENT_INSTRUCTIONS_CORRECT: 'settlement.instructions.correct',
    SETTLEMENT_INSTRUCTIONS_CANCEL: 'settlement.instructions.cancel',
    CUSTODY_BALANCES_LIST: 'custody.balances.list',
    SETTLEMENT_FAILURES_LIST: 'settlement.failures.list',
    // P2 hardening — accounting operations
    JOURNALS_CREATE: 'accounting.journal.create',
    JOURNALS_LIST: 'accounting.journal.list',
    JOURNALS_GET: 'accounting.journal.get',
    CHART_OF_ACCOUNTS_LIST: 'accounting.chartOfAccounts.list',
    FISCAL_PERIODS_LIST: 'accounting.fiscalPeriod.list',
    ACCOUNTING_FISCAL_YEAR_LIST: 'accounting.fiscalYear.list',
    ACCOUNTING_FISCAL_YEAR_CREATE: 'accounting.fiscalYear.create',
    ACCOUNTING_FISCAL_YEAR_GET: 'accounting.fiscalYear.get',
    ACCOUNTING_FISCAL_YEAR_CLOSE: 'accounting.fiscalYear.close',
    ACCOUNTING_FISCAL_YEAR_REOPEN: 'accounting.fiscalYear.reopen',
    ACCOUNTING_FISCAL_PERIODS_GENERATE: 'accounting.fiscalPeriods.generate',
    // P2 hardening — compliance and risk operations
    COMPLIANCE_BREACHES_LIST: 'compliance.breaches.list',
    COMPLIANCE_BREACHES_GET: 'compliance.breaches.get',
    RISK_EXPOSURES_LIST: 'risk.exposures.list',
    // P2 hardening — tax operations
    TAX_RECORDS_LIST: 'tax.records.list',
    TAX_CALCULATIONS_LIST: 'tax.calculations.list',
    // P2 hardening — evidence operations
    EVIDENCE_LIST: 'evidence.list',
    EVIDENCE_GET: 'evidence.get',
    EVIDENCE_UPLOAD: 'evidence.upload',
    // P2 hardening — org admin missing operations
    ORGANIZATION_APPROVAL_POLICIES_LIST: 'organization.approval-policies.list',
    ORGANIZATION_APPROVAL_POLICIES_UPDATE: 'organization.approval-policies.update',
    ORGANIZATION_PERMISSION_BUNDLES_LIST: 'organization.permission-bundles.list',
    ORGANIZATION_PERMISSION_BUNDLES_CREATE: 'organization.permission-bundles.create',
    ORGANIZATION_PERMISSION_BUNDLES_UPDATE: 'organization.permission-bundles.update',
    ORGANIZATION_RETENTION_POLICIES_READ: 'organization.retention-policies.read',
    ORGANIZATION_RETENTION_POLICIES_UPDATE: 'organization.retention-policies.update',
    ORGANIZATION_EXPORT_POLICIES_READ: 'organization.export-policies.read',
    ORGANIZATION_EXPORT_POLICIES_UPDATE: 'organization.export-policies.update',
    ORGANIZATION_SECURITY_SETTINGS_READ: 'organization.security-settings.read',
    ORGANIZATION_SECURITY_SETTINGS_UPDATE: 'organization.security-settings.update',
    ORGANIZATION_REGULATORY_PROFILE_READ: 'organization.regulatory-profile.read',
    ORGANIZATION_REGULATORY_PROFILE_UPDATE: 'organization.regulatory-profile.update',
    ORGANIZATION_JURISDICTION_SETTINGS_READ: 'organization.jurisdiction-settings.read',
    ORGANIZATION_JURISDICTION_SETTINGS_UPDATE: 'organization.jurisdiction-settings.update',
    ORGANIZATION_SUPPORT_ACCESS_READ: 'organization.support-access.read',
    ORGANIZATION_SUPPORT_ACCESS_MANAGE: 'organization.support-access.manage',
    ORGANIZATION_ACCESS_INSPECTOR_READ: 'organization.access-inspector.read',
    // P2 hardening — platform admin missing operations
    PLATFORM_SECURITY_POSTURE_READ: 'platform.security-posture.read',
    PLATFORM_DATA_PROVIDER_HEALTH_READ: 'platform.data-provider-health.read',
    // Opening cutover governance
    ACCOUNTING_OPENING_CUTOVER_PREVIEW: 'accounting.openingCutover.preview',
    ACCOUNTING_OPENING_CUTOVER_APPROVE: 'accounting.openingCutover.approve',
    ACCOUNTING_OPENING_CUTOVER_GET: 'accounting.openingCutover.get',
    ACCOUNTING_OPENING_CUTOVER_REVERSE: 'accounting.openingCutover.reverse',
    ACCOUNTING_OPENING_CUTOVER_RESTATE: 'accounting.openingCutover.restate',
    // Tax rule governance
    TAX_RULE_LIST: 'organization.tax-rules.list',
    TAX_RULE_READ: 'organization.tax-rules.read',
    TAX_RULE_CREATE_VERSION: 'organization.tax-rules.create-version',
    TAX_RULE_SUBMIT: 'organization.tax-rules.submit',
    TAX_RULE_APPROVE: 'organization.tax-rules.approve',
    TAX_RULE_SUSPEND: 'organization.tax-rules.suspend',
    TAX_RULE_RETIRE: 'organization.tax-rules.retire',
    TAX_RULE_IMPACT_ANALYZE: 'organization.tax-rules.impact-analyze',
    // Fee rule governance
    FEE_RULE_LIST: 'fee.rules.list',
    FEE_RULE_READ: 'fee.rules.read',
    FEE_RULE_CREATE_VERSION: 'fee.rules.create-version',
    FEE_RULE_SUBMIT: 'fee.rules.submit',
    FEE_RULE_APPROVE: 'fee.rules.approve',
    FEE_RULE_SUSPEND: 'fee.rules.suspend',
    FEE_RULE_RETIRE: 'fee.rules.retire',
    FEE_RULE_IMPACT_ANALYZE: 'fee.rules.impact-analyze',
};
/** All route IDs for parity tests. */
export const ALL_ROUTE_IDS = Object.values(ROUTE_IDS);
/** All icon IDs for parity tests. */
export const ALL_ICON_IDS = Object.values(ICON_IDS);
